import { autoinject, computedFrom } from 'aurelia-framework';
import { Router, RouterConfiguration } from 'aurelia-router';
import { ValidationRules } from 'aurelia-validation';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { AureliaHandler } from 'framework/error_handling/aurelia-handler';
import { log } from 'framework/script_utilities/dev-decorators';
import { TaskCounter, TaskMutex } from 'framework/script_utilities/task-executor';
import moment from 'moment';
import { EnumsService } from 'services/enums-service';
import { IdentityService } from 'services/identity-service';
import { KeywordsService } from 'services/keywords-service';
import { LookupsService } from 'services/lookups-service';
import { SafetyService } from 'services/safety-service';
import { ServiceBase } from 'support/base_classes/service-base';
import * as E from 'support/errors';
import { ERROR_ROUTE, HOME_ROUTE, MAX_DATE } from 'support/settings';
import { ToastHelper } from 'support/toaster';
import { FeatureService } from './services/features-service';
import { EventAggregator } from 'aurelia-event-aggregator';
import { TelemetryService } from 'services/telemetry-service';
import { TravelRequestsService } from 'services/travel-requests-service';
import { DeviationRequestsService } from 'services/deviation-requests-service';

@autoinject
export class App {
  router: Router;

  constructor(public taskMutex: TaskMutex, public taskCounter: TaskCounter, errorHandler: AureliaHandler, toastHelper: ToastHelper, dialogHelper: DialogHelper, private identity: IdentityService, private enums: EnumsService, private keywords: KeywordsService, private lookups: LookupsService, private safety: SafetyService, private features: FeatureService, private eventAggregator: EventAggregator, private telemetry: TelemetryService, private travelRequestsService: TravelRequestsService, private deviationRequestsService: DeviationRequestsService) {
    errorHandler
      .standardConfiguration()
      .addPredicatePresenter(e => e['customType'] === 'businessCheckError', e => dialogHelper.alert({ dialogTitle: 'Server validation failed', message: e['title'], details: e['validationErrors'] != null ? Object.getOwnPropertyNames(e['validationErrors']).flatMap(key => e['validationErrors'][key]).filter((v, i, a) => a.indexOf(v) === i) : null, class: '--warning', icon: 'fas fa-hand-paper', closeOnNavigation: true }))
      .addPredicatePresenter(e => e['message'] === 'ResizeObserver loop limit exceeded' || e['message'] === 'ResizeObserver loop completed with undelivered notifications.', e => console.warn(e))
      .addCommonAdapter(e => e['customType'] === 'authorizationError' ? { message: E.AUTHORIZATION_ERROR_MESSAGE, title: E.AUTHORIZATION_ERROR_TITLE } : void 0)
      .addCommonAdapter(e => e['customType'] === 'entityNotFoundError' ? { message: E.EXISTENCE_ERROR_MESSAGE, title: E.EXISTENCE_ERROR_TITLE } : void 0)
      .addCommonAdapter(e => e['message'] === 'Failed to fetch' || e['details']?.message === 'Failed to fetch' && this.router.currentInstruction.config.route === ERROR_ROUTE ? { message: E.FETCH_ERROR_MESSAGE, title: E.FETCH_ERROR_TITLE, details: null } : void 0)
      .useDefaultPresenter(e => e.ambient ? toastHelper.error(e) : dialogHelper.error({ dialogTitle: e.title, message: e.message, details: e.details }));
  }

  configureRouter(config: RouterConfiguration, router: Router) {
    config.title = 'TravelSafe';
    config.fallbackRoute(ERROR_ROUTE);
    config.map([
      { route: '', redirect: HOME_ROUTE },
      { route: HOME_ROUTE, moduleId: 'screens/home-index', name: 'home', title: 'Home' },
      { route: ERROR_ROUTE, moduleId: 'screens/system-error', name: 'error', title: 'Error' }
    ]);

    this.router = router;
  }

  @log('debug')
  async activate() {
    ValidationRules.customRule('minDate', dateString => dateString == null || moment.utc(dateString) >= moment().startOf('day'), '\${$displayName} must be today or later');
    ValidationRules.customRule('maxDate', dateString => dateString == null || moment.utc(dateString) <= moment(MAX_DATE), '\${$displayName} cannot be later than 31 Dec 2099');
    await this.initializeServices(this.identity);
    await this.initializeServices(this.enums);
    await this.initializeServices(this.keywords, this.telemetry);
    await this.initializeServices(this.lookups, this.safety, this.features);
    await this.initializeServices(this.travelRequestsService, this.deviationRequestsService);
  }

  @computedFrom('router.isNavigating', 'taskMutex.isRunning')
  get blockMouse() {
    return this.router.isNavigating || this.taskMutex.isRunning;
  }

  @computedFrom('router.isNavigating', 'taskCounter.runningCount')
  get showIndicator() {
    return this.router.isNavigating || this.taskCounter.runningCount > 0;
  }

  private async initializeServices(...services: ServiceBase[]) {
    try { await Promise.all(services.map(s => s.initialize())); }
    catch { }
  }
}
